import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { webinarService } from '../../../services/webinarService';

const WebinarDetailsModal = ({ isOpen, onClose, webinar }) => {
  const [registrations, setRegistrations] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen && webinar?.id) {
      loadRegistrations();
    }
  }, [isOpen, webinar]);

  const loadRegistrations = async () => {
    try {
      setLoading(true);
      const data = await webinarService?.getWebinarRegistrations(webinar?.id);
      setRegistrations(data || []);
    } catch (error) {
      console.error('Error loading registrations:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen || !webinar) return null;

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date?.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-card rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-card border-b border-border px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-heading font-semibold text-foreground">
            Webinar Details
          </h2>
          <button
            onClick={onClose}
            className="p-1 hover:bg-muted rounded transition-colors"
          >
            <Icon name="X" size={20} color="var(--color-foreground)" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Webinar Info */}
          <div>
            <h3 className="text-lg font-semibold text-foreground mb-4">{webinar?.title}</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-muted-foreground mb-1">Expert</p>
                <p className="text-sm font-medium text-foreground">{webinar?.expert?.name}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground mb-1">Topic</p>
                <p className="text-sm font-medium text-foreground">{webinar?.topic}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground mb-1">Scheduled</p>
                <p className="text-sm font-medium text-foreground">{formatDate(webinar?.scheduled_at)}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground mb-1">Duration</p>
                <p className="text-sm font-medium text-foreground">{webinar?.duration_minutes} minutes</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground mb-1">Status</p>
                <p className="text-sm font-medium text-foreground capitalize">{webinar?.status}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground mb-1">Participants</p>
                <p className="text-sm font-medium text-foreground">
                  {webinar?.participant_count || 0}
                  {webinar?.max_participants && ` / ${webinar?.max_participants}`}
                </p>
              </div>
            </div>
            {webinar?.description && (
              <div className="mt-4">
                <p className="text-sm text-muted-foreground mb-1">Description</p>
                <p className="text-sm text-foreground">{webinar?.description}</p>
              </div>
            )}
          </div>

          {/* Registrations */}
          <div>
            <h4 className="text-base font-semibold text-foreground mb-3">
              Registrations ({registrations?.length})
            </h4>
            {loading ? (
              <div className="text-center py-4">
                <div className="w-6 h-6 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto" />
              </div>
            ) : registrations?.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">
                No registrations yet
              </p>
            ) : (
              <div className="bg-muted/50 border border-border rounded-lg overflow-hidden">
                <div className="max-h-64 overflow-y-auto">
                  <table className="w-full">
                    <thead className="bg-muted sticky top-0">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-semibold text-foreground">Student</th>
                        <th className="px-4 py-2 text-left text-xs font-semibold text-foreground">Email</th>
                        <th className="px-4 py-2 text-left text-xs font-semibold text-foreground">Status</th>
                        <th className="px-4 py-2 text-left text-xs font-semibold text-foreground">Registered</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {registrations?.map((reg) => (
                        <tr key={reg?.id}>
                          <td className="px-4 py-2 text-sm text-foreground">{reg?.student?.name}</td>
                          <td className="px-4 py-2 text-sm text-foreground">{reg?.student?.email}</td>
                          <td className="px-4 py-2">
                            <span className="text-xs px-2 py-1 bg-primary/10 text-primary rounded-full capitalize">
                              {reg?.status}
                            </span>
                          </td>
                          <td className="px-4 py-2 text-sm text-muted-foreground">
                            {formatDate(reg?.registered_at)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>

          <div className="flex justify-end">
            <Button variant="outline" onClick={onClose}>
              Close
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WebinarDetailsModal;